import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MarketingButton, marketingButtonVariants } from "@appcaire/ui";
import { AnalysisCta } from "../../AnalysisCta";
import { trackMarketingCta } from "@/lib/marketingCta";
import {
  bookDemoHref,
  publicNackaDemoHref,
  BOOK_DEMO_REL,
  BOOK_DEMO_TARGET,
} from "@/config/platformAccess";
import { getLocalizedPath } from "@/utils/routes";
import type { LandingLocale } from "../landingCopy";
import { homeCopy } from "../homeCopy";
import type { AnalysisCtaId } from "@appcaire/shared/analysis/analysisEntry";

export type HomeSectionProps = { locale: LandingLocale };

/** Brand 2.2 page section: one container width and vertical rhythm. */
export const homeSectionClass =
  "mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8";
/** Section head: title left, lead right on wide screens; the lead is muted. */
export const homeSectionHeadClass =
  "mb-8 grid items-center gap-2 text-caire-muted md:grid-cols-2 md:gap-12";
/** Section h2 on the theme type scale. */
export const homeSectionTitleClass =
  "text-3xl font-semibold tracking-tight text-caire-ink sm:text-4xl";

/** The shared Brand 2.2 text link: action colour, arrow, underline on hover. */
export const homeLinkClass =
  "inline-flex items-center gap-1.5 font-semibold text-caire-action underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caire-action focus-visible:ring-offset-2";

/** One descriptive secondary link per section, at most. */
export function HomeLink({
  to,
  locale,
  children,
  external = false,
}: {
  to: string;
  locale: LandingLocale;
  children: string;
  external?: boolean;
}) {
  const content = (
    <>
      {children}
      <ArrowRight aria-hidden="true" size={18} />
    </>
  );
  if (external || to.startsWith("/images/") || to.startsWith("http"))
    return (
      <a
        href={to}
        className={homeLinkClass}
        {...(to.startsWith("/images/")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {content}
      </a>
    );
  return (
    <Link
      to={to.startsWith("/") ? to : getLocalizedPath(to, locale)}
      className={homeLinkClass}
    >
      {content}
    </Link>
  );
}

/**
 * The same actions, verbatim, in the hero and the closing section: the free
 * analysis and the platform overview as buttons, a walkthrough as a text
 * link, plus the owner's explicit read-only Nacka result entry. The app
 * owns current publication, flag and privacy admission.
 */
export function HomeActions({
  locale,
  cta,
}: {
  locale: LandingLocale;
  cta: AnalysisCtaId;
}) {
  const c = homeCopy[locale];
  const platformHref = getLocalizedPath("plattform", locale);
  return (
    <>
      <div
        className="flex flex-wrap items-center justify-center gap-3"
        data-home-actions
      >
        <AnalysisCta
          locale={locale}
          landing="home"
          cta={cta}
          label={c.primary}
          className={marketingButtonVariants({
            variant: "primary",
            size: "hero",
          })}
        />
        <MarketingButton asChild variant="secondary" size="hero">
          <Link
            to={platformHref}
            onClick={() =>
              trackMarketingCta(`platform_${cta}`, c.secondary, platformHref)
            }
            data-home-secondary
          >
            {c.secondary}
          </Link>
        </MarketingButton>
        <MarketingButton asChild variant="secondary" size="hero">
          <a
            href={publicNackaDemoHref(locale)}
            data-home-demo
            onClick={() =>
              trackMarketingCta(
                `demo_${cta}`,
                c.demo,
                publicNackaDemoHref(locale),
              )
            }
          >
            {c.demo}
          </a>
        </MarketingButton>
      </div>
      <p className="mt-3 text-center" data-home-booking>
        <Link
          to={bookDemoHref(locale)}
          target={BOOK_DEMO_TARGET}
          rel={BOOK_DEMO_REL}
          onClick={() =>
            trackMarketingCta(`booking_${cta}`, c.booking, bookDemoHref(locale))
          }
          className={homeLinkClass}
        >
          {c.booking}
          <ArrowRight aria-hidden="true" size={18} />
        </Link>
      </p>
    </>
  );
}
